/**
 * Milliseconds of leeway applied around the `exp` boundary.
 * A token whose `exp` is within this window in the past is still considered
 * valid, which tolerates minor clock drift between the JWT issuer and our servers.
 */
const CLOCK_SKEW_MS = 30 * 1000;

/**
 * Normalize a JWT `exp` claim to seconds since epoch. WX writes `exp` in
 * milliseconds, against RFC 7519, so a value above 1e12 is read as
 * milliseconds.
 */
export function normalizeJwtExpToSeconds(exp: number): number {
  return exp > 1e12 ? Math.floor(exp / 1000) : exp;
}

/**
 * Check whether a JWT is expired by inspecting the `exp` claim without
 * verifying the signature. Uses base64url decoding only — Edge-runtime safe.
 *
 * Returns true (expired/invalid) when:
 * - The token is not a valid three-part JWT
 * - The payload cannot be decoded or parsed
 * - `exp`, in seconds or milliseconds, is more than CLOCK_SKEW_MS in the past
 *
 * Returns false (valid) when there is no `exp` claim (lenient by design).
 */
export function isJwtExpiredEdge(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }
    // base64url → standard base64: swap URL-safe chars, then decode
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    if (!payload.exp) {
      return false; // No exp claim — be lenient
    }
    return (
      normalizeJwtExpToSeconds(payload.exp) * 1000 + CLOCK_SKEW_MS < Date.now()
    );
  } catch {
    return true; // Can't decode — treat as expired
  }
}
